<template>
  <main class="landing">
    <section class="pitch">
      <p class="eyebrow">Developmental editing</p>
      <h1>An editor's read of your whole manuscript</h1>
      <p>
        Twig reads your entire book 27+ times, analyzing it from a variety of perspectives, and gives you back the
        deliverables you need to get your book publish-ready: an editorial letter for the big picture changes, a
        revision plan checklist, and chapter notes to help you go section by section.
      </p>
      <p>
        Your manuscript stays yours. Nothing you upload is shared or used to train AI, and the story is always yours to
        write.
      </p>

      <div class="dg" aria-hidden="true">
        <div class="dg-report">
          <div class="dg-head">
            <span class="dg-tab on">Editorial Letter</span><span class="dg-tab">Chapter Notes</span
            ><span class="dg-tab">Revision Plan</span>
          </div>
          <p class="dg-title">The middle is carrying two books</p>
          <div class="dg-line" style="width: 92%"></div>
          <div class="dg-line" style="width: 84%"></div>
          <div class="dg-line" style="width: 61%"></div>
          <p class="dg-title">Nora's want is clear; her need is not</p>
          <div class="dg-line" style="width: 88%"></div>
          <div class="dg-line" style="width: 47%"></div>
          <div class="dg-note">
            <span class="dg-chip">Chapter 12</span>The reveal lands before the reader has a reason to care.
          </div>
        </div>
      </div>
    </section>

    <section class="landing-card">
      <transition name="step" mode="out-in">
        <!-- Step 1: email -->
        <div v-if="step === 1" key="email-step">
          <h2>{{ emailPrompt }}</h2>
          <p class="soft">Sign in, or create an account with your email.</p>
          <button type="button" class="btn wide google" :disabled="loading" @click="handleGoogleSignIn">
            <svg viewBox="0 0 24 24" class="google-icon" aria-hidden="true">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </button>
          <form class="form" @submit.prevent="handleEmailSubmit">
            <label class="field-label" for="auth-email">Email</label>
            <input
              id="auth-email"
              ref="emailInput"
              v-model="email"
              type="email"
              class="input"
              :class="{ 'has-error': error }"
              placeholder="name@example.com"
              required
              autocomplete="email"
            />
            <p v-if="error" class="error-message">{{ error }}</p>
            <button type="submit" class="btn primary wide" :disabled="!email || loading">
              {{ loading ? 'Please wait…' : 'Continue' }}
            </button>
          </form>
        </div>

        <!-- Step 2: password -->
        <div v-else-if="step === 2" key="password-step">
          <button type="button" class="back-button" :disabled="loading" @click="goBack">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              width="14"
              height="14"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z"
                clip-rule="evenodd"
              />
            </svg>
            {{ email }}
          </button>
          <h2>{{ passwordPrompt }}</h2>
          <p class="soft">{{ isSignUp ? 'At least 6 characters.' : 'Same account as arcs.twig.io.' }}</p>
          <form class="form" @submit.prevent="handlePasswordSubmit">
            <label class="field-label" for="auth-password">Password</label>
            <input
              id="auth-password"
              ref="passwordInput"
              v-model="password"
              type="password"
              class="input"
              :class="{ 'has-error': error }"
              required
              :autocomplete="isSignUp ? 'new-password' : 'current-password'"
              minlength="6"
            />
            <p v-if="resetSent" class="success-message">Check your email for a reset link.</p>
            <p v-else-if="error" class="error-message">{{ error }}</p>
            <button type="submit" class="btn primary wide" :disabled="!password || loading">
              {{ loading ? 'Please wait…' : isSignUp ? 'Create account' : 'Sign in' }}
            </button>
            <button
              v-if="!isSignUp && !resetSent"
              type="button"
              class="link-button"
              :disabled="loading"
              @click="handleForgotPassword"
            >
              Forgot your password?
            </button>
          </form>
          <p class="note">By {{ termsActionText }}, you agree to our Terms of Service and Privacy Policy.</p>
        </div>
      </transition>
    </section>

    <p class="landing-foot">
      Twig also makes <a href="https://arcs.twig.io">Arcs</a>, a plot board for novelists, in a private alpha.
    </p>
  </main>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const step = ref(1);
const email = ref('');
const password = ref('');
const isSignUp = ref(false);
const loading = ref(false);
const error = ref('');
const resetSent = ref(false);

const emailInput = ref<HTMLInputElement>();
const passwordInput = ref<HTMLInputElement>();

// Context-aware messaging based on route query
const context = computed(() => (route.query['context'] as string) || 'login');

const emailPrompt = computed(() => {
  if (context.value === 'edit') {
    return 'Where would you like your edit emailed to?';
  }
  return 'Enter your email';
});

const passwordPrompt = computed(() => {
  if (isSignUp.value) {
    if (context.value === 'edit') {
      return 'Create a password to protect your edit';
    } else {
      return 'Create a password to protect your account';
    }
  }
  return 'Enter your account password';
});

const termsActionText = computed(() => (isSignUp.value ? 'creating an account' : 'using this service'));

// Focus inputs when step changes
watch(step, async newStep => {
  await nextTick();
  // Wait for transition to complete (300ms as defined in CSS)
  setTimeout(() => {
    if (newStep === 1) {
      emailInput.value?.focus();
    } else if (newStep === 2) {
      passwordInput.value?.focus();
    }
  }, 350);
});

// Auto-focus on mount
nextTick(() => {
  emailInput.value?.focus();
});

async function handleEmailSubmit() {
  error.value = '';
  loading.value = true;

  try {
    // Check if email exists in the system
    const exists = await authStore.checkEmailExists(email.value);
    isSignUp.value = !exists;
    step.value = 2;
  } catch (err) {
    error.value = 'Failed to check email. Please try again.';
  } finally {
    loading.value = false;
  }
}

async function handlePasswordSubmit() {
  error.value = '';
  loading.value = true;

  try {
    if (isSignUp.value) {
      await authStore.signUpWithEmail(email.value, password.value);
    } else {
      await authStore.signInWithEmail(email.value, password.value);
    }
    redirectAfterAuth();
  } catch (err: any) {
    if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
      error.value = 'Wrong password. Double-check and try again.';
    } else if (err.code === 'auth/user-not-found') {
      error.value = 'No account found with this email.';
    } else if (err.code === 'auth/weak-password') {
      error.value = 'Password must be at least 6 characters.';
    } else if (err.code === 'auth/email-already-in-use') {
      error.value = "This email already has an account. If you deleted it, email support@twig.io and we'll restore it.";
    } else if (err.code === 'auth/too-many-requests') {
      error.value = 'Too many attempts. Take a breather and try again in a few minutes.';
    } else {
      error.value = 'Something went wrong. Please try again.';
    }
  } finally {
    loading.value = false;
  }
}

async function handleForgotPassword() {
  error.value = '';
  loading.value = true;
  try {
    await authStore.resetPassword(email.value);
    resetSent.value = true;
  } catch {
    error.value = 'Failed to send reset email. Please try again.';
  } finally {
    loading.value = false;
  }
}

async function handleGoogleSignIn() {
  error.value = '';
  loading.value = true;

  try {
    await authStore.signInWithGoogle();
    redirectAfterAuth();
  } catch (err: any) {
    error.value = err.message || 'Google sign-in failed. Please try again.';
  } finally {
    loading.value = false;
  }
}

function goBack() {
  error.value = '';
  password.value = '';
  step.value = 1;
}

function redirectAfterAuth() {
  const redirect = route.query['redirect'] as string;
  if (redirect) {
    router.push(redirect);
  } else {
    router.push('/edits');
  }
}
</script>

<style scoped>
/* The same page as arcs.twig.io's sign-in: the pitch and an illustration on the left, one card on the right. */
.landing {
  max-width: 1100px;
  margin: 0 auto;
  padding: 72px 32px 32px;
  display: grid;
  grid-template-columns: minmax(0, 55fr) minmax(0, 40fr);
  column-gap: 64px;
  row-gap: 40px;
  align-items: start;
}

.landing-foot {
  grid-column: 1 / -1;
  margin: 0;
  text-align: center;
  font-size: 13px;
  color: #8b949e;
}

.landing-foot a {
  color: #1a5f8a;
  text-decoration: none;
}

.landing-foot a:hover {
  text-decoration: underline;
}

.pitch .eyebrow {
  margin: 0 0 12px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  font-weight: 600;
  color: #8b949e;
}

.pitch h1 {
  margin: 0 0 24px;
  font-family: 'PT Serif', Georgia, 'Times New Roman', serif;
  font-size: 40px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.01em;
  text-wrap: balance;
  color: #24292f;
}

.pitch > p {
  margin: 0 0 16px;
  font-size: 15px;
  line-height: 1.55;
  color: #57606a;
  max-width: 60ch;
}

/* the illustration: an edit report, understated */
.dg {
  margin-top: 32px;
  background: white;
  border: 1px solid #e1e4e8;
  border-radius: 12px;
  padding: 20px 24px 22px;
  box-shadow:
    0 1px 2px rgba(31, 41, 51, 0.06),
    0 2px 8px rgba(31, 41, 51, 0.06);
  user-select: none;
}

.dg-head {
  display: flex;
  gap: 6px;
  margin-bottom: 18px;
}

.dg-tab {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 999px;
  border: 1px solid #d0d7de;
  color: #57606a;
}

.dg-tab.on {
  background: rgba(97, 174, 212, 0.1);
  border-color: #61aed4;
  color: #1a5f8a;
}

.dg-title {
  margin: 14px 0 8px;
  font-family: 'PT Serif', Georgia, serif;
  font-size: 14px;
  font-weight: 700;
  color: #24292f;
}

.dg-title:first-of-type {
  margin-top: 0;
}

.dg-line {
  height: 7px;
  border-radius: 4px;
  background: #eef2f5;
  margin-bottom: 7px;
}

.dg-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #f6f8fa;
  font-size: 12px;
  color: #57606a;
}

.dg-chip {
  flex: none;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  background: rgba(239, 175, 3, 0.12);
  color: #8a6400;
}

.landing-card {
  background: white;
  border: 1px solid #e1e4e8;
  border-radius: 12px;
  padding: 28px;
  box-shadow:
    0 1px 2px rgba(31, 41, 51, 0.06),
    0 2px 8px rgba(31, 41, 51, 0.06);
}

.landing-card h2 {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0;
  color: #24292f;
}

.soft {
  margin: 0 0 16px;
  font-size: 12.5px;
  color: #8b949e;
}

.form {
  display: block;
}

.field-label {
  display: block;
  font-size: 11.5px;
  color: #57606a;
  margin: 12px 0 4px;
  font-weight: 500;
}

.input {
  width: 100%;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  padding: 7px 9px;
  background: white;
  font-size: 14px;
  font-family: inherit;
  color: #24292f;
}

.input:focus {
  outline: none;
  border-color: #61aed4;
  box-shadow: 0 0 0 3px rgba(97, 174, 212, 0.1);
}

.input.has-error {
  border-color: #cf222e;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid #d0d7de;
  background: white;
  color: #24292f;
  box-shadow: 0 1px 2px rgba(31, 41, 51, 0.06);
  cursor: pointer;
  font-family: inherit;
  transition:
    transform 0.12s ease,
    box-shadow 0.12s ease;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow:
    0 1px 2px rgba(31, 41, 51, 0.06),
    0 2px 8px rgba(31, 41, 51, 0.06);
}

.btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.btn.primary {
  background: #1a5f8a;
  color: white;
  border-color: transparent;
  font-weight: 600;
}

.btn.wide {
  width: 100%;
}

.form .btn.primary.wide {
  margin-top: 18px;
}

.google-icon {
  width: 16px;
  height: 16px;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 14px;
  padding: 0;
  border: 0;
  background: none;
  font-size: 12.5px;
  color: #57606a;
  cursor: pointer;
  font-family: inherit;
}

.back-button:hover:not(:disabled) {
  color: #24292f;
}

.link-button {
  display: block;
  margin: 12px auto 0;
  padding: 0;
  border: 0;
  background: none;
  font-size: 12.5px;
  color: #1a5f8a;
  cursor: pointer;
  font-family: inherit;
}

.link-button:hover:not(:disabled) {
  text-decoration: underline;
}

.note {
  margin: 18px 0 0;
  font-size: 12px;
  line-height: 1.45;
  color: #8b949e;
}

.error-message {
  margin: 10px 0 0;
  font-size: 13px;
  color: #cf222e;
}

.success-message {
  margin: 10px 0 0;
  font-size: 13px;
  color: #1a7f37;
}

.step-enter-active,
.step-leave-active {
  transition: opacity 0.15s ease-out;
}

.step-enter-from,
.step-leave-to {
  opacity: 0;
}

@media (max-width: 900px) {
  .landing {
    grid-template-columns: 1fr;
    padding: 40px 16px 24px;
    row-gap: 28px;
  }

  .pitch h1 {
    font-size: 32px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .step-enter-active,
  .step-leave-active,
  .btn {
    transition: none;
  }
}
</style>
